import {
  Award, Book, Brush2, Building, ClipboardTick, Category, Chart, Chart2, ChemicalGlass, Code, Coin, DirectInbox, DocumentText1, Driver, Element4,
  Flag, Headphone, Hierarchy, MagicStar, Messages2, MessageText, Moneys, People, Personalcard, Receipt, Routing2,
  Setting2, ShieldTick, TaskSquare, Ticket, VolumeHigh, type Icon,
} from '@/lib/iconsax';

export type NavItem = { to: string; label: string; key: string; icon: Icon; perm?: string | string[]; end?: boolean };
export type NavGroup = { title?: string; key?: string; items: NavItem[] };

/** Customer panel. Paths are relative to /w/:slug. Settings lives in the sidebar footer. */
export const APP_NAV: NavGroup[] = [
  { items: [
    { to: '', label: 'Dashboard', key: 'nav.home', icon: Category, end: true },
    { to: '/onboarding', label: 'Get started', key: 'nav.onboarding', icon: TaskSquare, perm: 'workspace.manage' },
  ] },
  { title: 'Build', key: 'nav.group.build', items: [
    { to: '/agents', label: 'AI agents', key: 'nav.agents', icon: MagicStar, perm: 'agents.read' },
    { to: '/knowledge', label: 'Knowledge', key: 'nav.knowledge', icon: Book, perm: 'knowledge.read' },
    { to: '/flows', label: 'Flow builder', key: 'nav.flows', icon: Hierarchy, perm: 'flows.read' },
    { to: '/test-lab', label: 'Test lab', key: 'nav.testlab', icon: ChemicalGlass, perm: 'agents.read' },
  ] },
  { title: 'Engage', key: 'nav.group.engage', items: [
    { to: '/inbox', label: 'Inbox', key: 'nav.inbox', icon: DirectInbox, perm: ['inbox.read_all', 'inbox.read_assigned'] },
    { to: '/tickets', label: 'Tickets', key: 'nav.tickets', icon: Ticket, perm: 'tickets.read' },
    { to: '/contacts', label: 'Contacts', key: 'nav.contacts', icon: Personalcard, perm: 'contacts.read' },
    { to: '/leads', label: 'Leads', key: 'nav.leads', icon: Chart, perm: 'contacts.read' },
  ] },
  { title: 'Automate', key: 'nav.group.automate', items: [
    { to: '/workflows', label: 'Workflows', key: 'nav.workflows', icon: Routing2, perm: 'workflows.read' },
    { to: '/campaigns', label: 'Campaigns', key: 'nav.campaigns', icon: VolumeHigh, perm: 'campaigns.read' },
    { to: '/channels', label: 'Channels', key: 'nav.channels', icon: Messages2, perm: 'channels.read' },
    { to: '/integrations', label: 'Integrations', key: 'nav.integrations', icon: Element4, perm: 'channels.read' },
  ] },
  { title: 'Workspace', key: 'nav.group.workspace', items: [
    { to: '/analytics', label: 'Analytics', key: 'nav.analytics', icon: Chart2, perm: 'analytics.read' },
    { to: '/team', label: 'Team', key: 'nav.team', icon: People, perm: 'members.read' },
    { to: '/billing', label: 'Billing', key: 'nav.billing', icon: Moneys, perm: 'billing.read' },
    { to: '/developers', label: 'API & webhooks', key: 'nav.developers', icon: Code, perm: 'developers.manage' },
    { to: '/branding', label: 'Branding', key: 'nav.branding', icon: Brush2, perm: 'workspace.manage' },
  ] },
];

/** Agent desk. Paths relative to /w/:slug/desk. */
export const DESK_NAV: NavItem[] = [
  { to: '', label: 'My inbox', key: 'desk.mine', icon: DirectInbox, end: true },
  { to: '/queue', label: 'Team queue', key: 'desk.queue', icon: Headphone },
  { to: '/customers', label: 'Customer 360', key: 'desk.customers', icon: Personalcard, perm: 'contacts.read' },
  { to: '/tickets', label: 'Tickets', key: 'desk.tickets', icon: Ticket, perm: 'tickets.read' },
  { to: '/replies', label: 'Saved replies', key: 'desk.replies', icon: MessageText },
  { to: '/stats', label: 'My performance', key: 'desk.stats', icon: Award },
];

/** Platform super admin. Paths relative to /admin. */
export const ADMIN_NAV: NavGroup[] = [
  { items: [{ to: '', label: 'Overview', key: 'admin.overview', icon: Category, end: true }] },
  { title: 'Customers', items: [
    { to: '/workspaces', label: 'Workspaces', key: 'admin.workspaces', icon: Building },
    { to: '/members', label: 'Users', key: 'admin.members', icon: People },
    { to: '/bots', label: 'Bots & channels', key: 'admin.bots', icon: Messages2 },
  ] },
  { title: 'Revenue', items: [
    { to: '/plans', label: 'Plans', key: 'admin.plans', icon: TaskSquare },
    { to: '/subscriptions', label: 'Subscriptions', key: 'admin.subscriptions', icon: Coin },
    { to: '/invoices', label: 'Invoices', key: 'admin.invoices', icon: Receipt },
    { to: '/usage', label: 'Usage & revenue', key: 'admin.usage', icon: Chart2 },
    { to: '/ai-costs', label: 'AI provider costs', key: 'admin.aicosts', icon: MagicStar },
  ] },
  { title: 'Platform', items: [
    { to: '/operations', label: 'Operations', key: 'admin.operations', icon: Driver, perm: 'platform.operations.read' },
    { to: '/webhook-logs', label: 'Webhook logs', key: 'admin.webhooks', icon: DocumentText1, perm: 'platform.operations.read' },
    { to: '/security', label: 'Security & audit', key: 'admin.security', icon: ShieldTick },
    { to: '/flags', label: 'Feature flags', key: 'admin.flags', icon: Flag },
    { to: '/uat', label: 'UAT runner', key: 'admin.uat', icon: ClipboardTick, perm: 'platform.read' },
    { to: '/settings', label: 'Platform settings', key: 'admin.settings', icon: Setting2 },
  ] },
];

/** Title shown in the top bar for a pathname (falls back to the closest nav item). */
export function titleFor(pathname: string, base: string, groups: NavGroup[] | NavItem[]): string | null {
  const items: NavItem[] = (groups as any[]).flatMap((g) => ('items' in g ? g.items : [g]));
  const rel = pathname.slice(base.length) || '';
  const match = items
    .filter((i) => (i.end ? rel === i.to || rel === `${i.to}/` : rel === i.to || rel.startsWith(`${i.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.label ?? null;
}
